import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { Spin } from 'antd'
import { setActiveVacancy } from '../../../redux/actions/searchProfessionActions'
import { formatRub, pluralN, salaryAverage } from './genUtils'
import s from './gen.module.scss'
import p from '../../SearchProfession/ProfReview/ProfReview.module.scss'

// Слот для кнопок профессии («В избранное», «Описание») — их рисует шаг 1 (ProfReview) порталом.
export const SALARY_ACTIONS_ID = 'gen-salary-actions'

// Диаграмма как в прежней версии (ParentBlockProgressVertical): столбец = вакансия, по возрастанию
// зарплаты, высота — по месту в ряду, а не пропорционально сумме, поэтому один выброс не
// сплющивает остальные. Сумма подписана над каждым столбцом, работодатель — под ним.
const shortRub = (n) =>
  n >= 1000000 ? `${(n / 1000000).toFixed(1).replace('.0', '')} млн` : `${Math.round(n / 1000)} тыс`

export const SalaryChart = ({ vacancies, activeId, onPick, small }) => {
  const rows = vacancies
    .map((v) => ({ v, avg: salaryAverage(v) }))
    .filter((r) => r.avg)
    .sort((a, b) => a.avg - b.avg)
  if (!rows.length) return <div className={cn(s.muted, s.small)}>Работодатели не указали зарплату в этих вакансиях</div>
  const n = rows.length
  const dense = n > 14
  return (
    <div className={cn(p.chart, p.chartNoAxis, { [p.chartSm]: small, [p.chartDense]: dense })}>
      <div className={p.plot}>
        {[0, 25, 50, 75].map((t) => (
          <div key={t} className={p.grid} style={{ top: `${t}%` }} />
        ))}
        {rows.map(({ v, avg }, i) => {
          const on = activeId && v.id === activeId
          const company = v.employer?.name || 'Работодатель'
          return (
            <button
              type="button"
              key={v.id}
              className={cn(p.col, { [p.colOn]: on })}
              aria-label={`${v.name}, ${company}, ${formatRub(avg)}`}
              aria-pressed={!!on}
              onClick={() => onPick(v)}
            >
              <div className={p.tip}>
                <b>{company}</b> · {formatRub(avg)}
              </div>
              <i style={{ height: `${Math.round(((i + 1) / n) * 88)}%` }}>
                <em className={p.val}>{shortRub(avg)}</em>
              </i>
              <span>{company}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Зарплаты по вакансиям выбранной профессии — правая половина верхней зоны на шаге 1.
// Клик по столбцу выбирает вакансию; блок «Вакансии» ниже прокручивается к ней сам.
const SalaryPanel = () => {
  const dispatch = useDispatch()
  const { activeProfession, activeVacancy, vacancycData, flagLoadingVacancy } = useSelector(
    (state) => state.SearchProfession
  )
  const vacancies = (vacancycData || []).filter((v) => v && v.id)
  const withSalary = vacancies.filter((v) => salaryAverage(v))
  const topPaid = withSalary.reduce((best, v) => (salaryAverage(v) > (salaryAverage(best) || 0) ? v : best), null)
  const activeId =
    (activeVacancy && vacancies.some((v) => v.id === activeVacancy.id) && activeVacancy.id) || topPaid?.id

  return (
    <section className={s.salaryPanel} aria-label="Зарплатные возможности">
      <div className={s.salaryHead}>
        <div className={s.planHead}>
          <h3>Зарплатные возможности</h3>
          <span className={cn(s.muted, s.small)}>
            {activeProfession
              ? `${activeProfession.professionTitle}${
                  withSalary.length
                    ? ` · ${pluralN(withSalary.length, ['вакансия', 'вакансии', 'вакансий'])} с зарплатой`
                    : ''
                } · нажмите на столбец`
              : 'Выберите профессию — покажем зарплаты по вакансиям'}
          </span>
        </div>
        <div id={SALARY_ACTIONS_ID} className={s.salaryActions} />
      </div>
      {!activeProfession ? null : flagLoadingVacancy && !vacancies.length ? (
        <Spin style={{ margin: '24px auto' }} />
      ) : vacancies.length ? (
        <SalaryChart small vacancies={vacancies} activeId={activeId} onPick={(v) => dispatch(setActiveVacancy(v))} />
      ) : (
        <div className={cn(s.muted, s.small)}>Вакансий по этой профессии в выбранном регионе не нашлось</div>
      )}
    </section>
  )
}

export default SalaryPanel
